// PrintReport.js — wrapper around TestReportContent for the printable
// PDF report. Fetches data, captures three section divs as separate
// bitmaps, places them onto an A4 PDF with section-aware pagination,
// and stamps a Page X of Y footer onto every page.
//
// Sections, in order:
//   1. page1Ref — metadata + interpretation + disk table + record history
//      Allowed to spill across pages (rare; only with 12-row disk table
//      plus long notes). Slicer is the same as before.
//   2. imageRef — plate image, locked to 100mm × 100mm via the printMode
//      prop. ATOMIC: if it doesn't fit on the current page, addPage()
//      before placing. Never sliced mid-image.
//   3. page2Ref — revision audit table. Only rendered when there are 2+
//      revisions. Always starts on a fresh page.
//
// Capture-width strategy. The whole report is wrapped in a 720px-wide
// container (= A4 content area at 96 DPI, after 28pt margins). After
// html2canvas + jsPDF scaling, DOM pixel sizes land in the PDF at a
// predictable 1:1 conversion: 1.2rem (19.2px) → ~14.4pt. This stops
// the implicit DOM-to-PDF rescaling that previously made fonts read
// small in tall reports.

import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getTestResultById,
  getUserProfile,
  getOrganizationDetails,
  getSpecimenTypes,
  getClinicalIndications,
  getSpeciesMaster,
} from "../firestoreService";
import { loadRevisionHistory } from "./loadRevisionHistory";
import TestReportContent from "./TestReportContent";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import { Box, Button, CircularProgress, Paper, Alert } from "@mui/material";

function PrintReport() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [submitterName, setSubmitterName] = useState("");
  const [editorName, setEditorName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [primaryGuideline, setPrimaryGuideline] = useState("EUCAST");
  // Raw arrays from the master collections. Lookup maps are derived via
  // useMemo so they rebuild on language change without refetching.
  const [allSpecimenTypes, setAllSpecimenTypes] = useState([]);
  const [allClinicalIndications, setAllClinicalIndications] = useState([]);
  const [allSpecies, setAllSpecies] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [editorNames, setEditorNames] = useState({});

  const page1Ref = useRef();
  const imageRef = useRef();
  const page2Ref = useRef();

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchTest = async () => {
      if (!testId) return;
      setLoading(true);
      try {
        const testData = await getTestResultById(testId);
        setTest(testData);

        if (testData) {
          const [
            submitter,
            org,
            specimenTypes,
            clinicalIndications,
            species,
            history,
          ] = await Promise.all([
            getUserProfile(testData.submitterUid),
            getOrganizationDetails(testData.organizationId),
            getSpecimenTypes(),
            getClinicalIndications(),
            getSpeciesMaster(),
            loadRevisionHistory(testId),
          ]);
          setSubmitterName(submitter?.displayName || "Unknown");
          setOrganizationName(org?.name || "Unknown");
          setPrimaryGuideline(org?.primaryGuideline || "EUCAST");

          // Store raw master-data arrays. Lookup maps are derived below via
          // useMemo so they rebuild on language change without refetching.
          setAllSpecimenTypes(specimenTypes || []);
          setAllClinicalIndications(clinicalIndications || []);
          setAllSpecies(species || []);

          setRevisions(history.revisions);
          setEditorNames(history.editorNames);

          // Tolerates the legacy field name `lastEditedBy` AND the
          // current Phase 3 field `lastEditedByUid`.
          const editorUid = testData.lastEditedByUid || testData.lastEditedBy;
          if (testData.isEdited && editorUid && typeof editorUid === "string") {
            const editor = await getUserProfile(editorUid);
            setEditorName(editor?.displayName || "Unknown user");
          }
        }
      } catch (err) {
        setError(t("printReport.loadError"));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId, t]);

  /**
   * Section-aware PDF generation. Walks page1Ref → imageRef → page2Ref
   * in order, tracking a cursor on the current page. Each section is
   * captured separately and placed with awareness of its placement
   * rules (atomic vs. allowed-to-spill, addPage-before vs. inline).
   *
   * After all sections are placed, a second pass writes "Page X of Y"
   * onto every page's footer area.
   */
  const handleDownloadPdf = async () => {
    if (!page1Ref.current) return;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const contentWidth = pdfWidth - margin * 2;
    // Reserve space at the bottom of every page for the Page X of Y
    // footer. Section content must stop above this band.
    const footerBand = 24;
    const usableHeight = pdfPageHeight - margin * 2 - footerBand;
    // Bottom-of-content y coordinate (in PDF pts). Sections can be
    // placed up to (but not exceeding) this y.
    const contentBottom = margin + usableHeight;

    // Capture a DOM node to a bitmap and return the data URL plus its
    // PDF dimensions (width fixed to contentWidth; height derived).
    const captureSection = async (node) => {
      const canvas = await html2canvas(node, {
        scale: 2.5,
        useCORS: true,
        logging: false,
      });
      const srcW = canvas.width;
      const srcH = canvas.height;
      const widthPt = contentWidth;
      const heightPt = (srcH / srcW) * contentWidth;
      return {
        canvas,
        srcW,
        srcH,
        widthPt,
        heightPt,
      };
    };

    // Cursor on the current page (PDF pts). Starts at top margin on
    // page 1 and resets to top margin on every addPage().
    let cursorY = margin;

    // Place a captured-section bitmap inline, splitting across pages if
    // it doesn't fit (slicing strategy retained from the prior code).
    // Caller must have ensured cursorY ≤ contentBottom.
    const placeSplittable = ({ canvas, srcW, srcH }) => {
      const pxPerPt = srcW / contentWidth;
      let sliceTop = 0;

      while (sliceTop < srcH) {
        const availablePtOnCurrent = contentBottom - cursorY;
        const availablePxOnCurrent = Math.floor(availablePtOnCurrent * pxPerPt);
        const sliceH = Math.min(availablePxOnCurrent, srcH - sliceTop);

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = srcW;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, srcW, sliceH);
        ctx.drawImage(canvas, 0, sliceTop, srcW, sliceH, 0, 0, srcW, sliceH);

        const imgData = sliceCanvas.toDataURL("image/jpeg", 1.0);
        const sliceHeightPt = sliceH / pxPerPt;

        pdf.addImage(
          imgData,
          "JPEG",
          margin,
          cursorY,
          contentWidth,
          sliceHeightPt,
        );

        sliceTop += sliceH;
        cursorY += sliceHeightPt;

        // If there's more to place, start a fresh page.
        if (sliceTop < srcH) {
          pdf.addPage();
          cursorY = margin;
        }
      }
    };

    // Place a captured-section bitmap atomically — never sliced. If it
    // doesn't fit on the current page, addPage() first. The plate image
    // is the only atomic section today.
    const placeAtomic = (section) => {
      if (cursorY + section.heightPt > contentBottom) {
        pdf.addPage();
        cursorY = margin;
      }
      const imgData = section.canvas.toDataURL("image/jpeg", 1.0);
      pdf.addImage(
        imgData,
        "JPEG",
        margin,
        cursorY,
        section.widthPt,
        section.heightPt,
      );
      cursorY += section.heightPt;
    };

    try {
      // Section 1: page1Ref (allowed to spill)
      const page1Section = await captureSection(page1Ref.current);
      placeSplittable(page1Section);

      // Section 2: imageRef (atomic — never split)
      if (imageRef.current) {
        const imageSection = await captureSection(imageRef.current);
        placeAtomic(imageSection);
      }

      // Section 3: page2Ref (audit table). Only when 2+ revisions.
      // Flows naturally after the image and is placed atomically — if
      // it doesn't fit on the current page, addPage() first and place
      // at the top of the next. Never split mid-table. Audit tables
      // are short enough that a single page is always enough.
      if (page2Ref.current && revisions.length > 1) {
        const page2Section = await captureSection(page2Ref.current);
        placeAtomic(page2Section);
      }

      // Second pass — stamp Page X of Y on every page's footer.
      const totalPages = pdf.internal.getNumberOfPages();
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        const label = t("printReport.pageOfTotal", {
          current: i,
          total: totalPages,
        });
        pdf.text(label, pdfWidth / 2, pdfPageHeight - 14, { align: "center" });
      }

      pdf.save(`KBQuick_Report_${test.caseId || test.id}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert(`Failed to generate PDF: ${err?.message || err}`);
    }
  };

  // Derived localization maps. Rebuilt on language change without refetching.
  // - specimenTypeMap is keyed by code (the persisted form on the test record).
  // - clinicalIndicationMap and speciesMap are keyed by display_name (English),
  //   which is what's stored on the test record per the English-only data
  //   layer policy.
  const specimenTypeMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const st of allSpecimenTypes) {
      if (st?.code) {
        m[st.code] = st.translations?.[lang] || st.displayName || st.code;
      }
    }
    return m;
  }, [allSpecimenTypes, i18n.language]);

  const clinicalIndicationMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const ci of allClinicalIndications) {
      if (ci?.displayName) {
        m[ci.displayName] = ci.translations?.[lang] || ci.displayName;
      }
    }
    return m;
  }, [allClinicalIndications, i18n.language]);

  const speciesMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const sp of allSpecies) {
      if (sp?.displayName) {
        m[sp.displayName] = sp.translations?.[lang] || sp.displayName;
      }
    }
    return m;
  }, [allSpecies, i18n.language]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!test)
    return <Alert severity="warning">{t("printReport.noTestData")}</Alert>;

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 1, displayPrint: "none" }}>
        <Button variant="contained" onClick={handleDownloadPdf}>
          {t("printReport.downloadPdf")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          {t("printReport.backToList")}
        </Button>
      </Box>

      {/* Fixed 1200px width. A4 content area is 539pt = 720px at 96 DPI,
          so 1200px makes the DOM-to-PDF scale ~0.45× — body text
          (1.2rem = 19.2px) lands at ~8.6pt in the PDF. Single dial:
          widen for smaller PDF fonts, narrow for larger. */}
      <Paper
        sx={{
          p: 4,
          width: "1200px",
          mx: "auto",
          "@media print": { boxShadow: "none", p: 0 },
        }}
      >
        <TestReportContent
          test={test}
          submitterName={submitterName}
          editorName={editorName}
          organizationName={organizationName}
          primaryGuideline={primaryGuideline}
          specimenTypeMap={specimenTypeMap}
          clinicalIndicationMap={clinicalIndicationMap}
          speciesMap={speciesMap}
          revisions={revisions}
          editorNames={editorNames}
          page1Ref={page1Ref}
          imageRef={imageRef}
          page2Ref={page2Ref}
          printMode={true}
        />
      </Paper>
    </>
  );
}

export default PrintReport;
